import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { GenericConfirmationDialogComponent } from '../../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';

import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { NzUploadFile } from 'ng-zorro-antd/upload';

import { finalize, map, tap, delay, take } from 'rxjs';
import { DateTime } from 'luxon';

import { FeUtil, TYPE, PERMISSION_CONFIG, BeUtil } from '../../../../hezky-routes';

import { IncidentFormPdfService } from '../../services/incident-form-pdf.service';

import * as XLSX from 'xlsx';

export interface Tab {
  tab_type: string,
  form_name: string,
  form_type: string,
  disabled?: boolean,
  canSubmit?: boolean,
  canApprove?: boolean,
  canReject?: boolean,
  canProduct?: boolean,
}
export interface History {
  incident_log_id: number;
  incident_id: number;
  leg_id: number;
  message_type: string;
  message: string;
  created_time: Date;
  remarks: null;
}
export interface MetaData {
  page: 'tr-incident' | 'storage-incident';
  id: number | string; // asset or trip id
  leg_id?: number | string;
  leg_status?: string;
  incident_id?: string;
  status?: string;
  severity?: ('high' | 'low' | 'medium') | number;
  is_assigned_to_user?: boolean;
  hide_forms?: boolean;
  extension?: {
    admin_id?: number | null;
    dueDate?: number | null;
    status?: 'in-progress' | 'pending' | 'expired' | 'extended' | 'to-be-expired';
    user_id?: number | null;
    reminder_date?: number | null;
  } | null;
  date: [string, string | null] | [];
  capabilities: string[];
}
export interface FormConfig {
  form_name: string,
  form_type: string,
  fields: TYPE.filterField[],
  columnWidth?: number
};
export interface Loaders {
  submit?: boolean,
  approve?: boolean,
  reject?: boolean,
  modal?: boolean,
  loading?: boolean
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-forms',
  templateUrl: './forms.component.html',
})
export class FormsComponent implements OnInit, OnChanges {
  @ViewChild('graphTemplate') graphTemplate!: ElementRef<HTMLDivElement>;
  @Input({ required: true }) metaData!: MetaData;
  @Input({ required: true }) config: FormConfig[] = [];
  @Input({ required: true, transform: (value: any) => Array.from(new Set(value.map((user: any) => user?.username))) }) users: string[] = [];
  @Output() reload: EventEmitter<'REFRESH-BY-ID'> = new EventEmitter<'REFRESH-BY-ID'>();

  formConfig: FormConfig[] = [];
  formGroup: FormGroup = new FormGroup({});
  tabs: Tab[] = [];
  affectedProductList: any[] = [];
  showProductTable: boolean = false;
  affectedProductForm: FormGroup = new FormGroup({ products: new FormArray([]) });
  hasDuplicateExists: boolean = false;
  masterProductList: any[] = [];
  currentTab: number = 0;
  actionDetails: { [key: string]: Record<string, string> } = {
    prod: {},
    capa: {},
    eval: {},
    final: {}
  };
  modals: { [key: string]: boolean } = {
    isRequestExtensionModalVisible: false,
    isCapaModalVisible: false,
    isProductModal: false,
    isFormReportModal: false
  };
  loaders: { [key: string]: Loaders } = {
    final: { approve: false, reject: false },
    prod: { submit: false, approve: false, reject: false },
    capa: { submit: false, approve: false, reject: false },
    eval: { submit: false, approve: false, reject: false },
    extension: { submit: false, modal: false },
    isFormLoading: { loading: false },
    isResubmit: { loading: false },
    isDownloading: { loading: false }
  };
  showFinal:boolean = false;
  history: History[] = [];
  capaFiles: NzUploadFile[] = [];
  rejectedCapaFiles: { doc_id: number, doc_name: string, doc_link: string }[] = [];
  qualityPersonnelList: any[] = [];
  extensionForm: FormGroup = new FormGroup({
    admin_id: new FormControl(null, [Validators.required]),
    flag: new FormControl(null),
    unit: new FormControl(null),
    type: new FormControl(null),
    remarks: new FormControl(null, [Validators.required, Validators.minLength(3)])
  });
  flag: 'request' | 'approved' | 'rejected' | null = null;
  lastRejectResubmitLogId: number[] = [];
  rejectedFormData: any | null = null;
  reportFormData: any | null = null;
  isViewReport: boolean = false;
  severityMap: Record<string, 'low' | 'medium' | 'high'> = {
    1: 'low',
    2: 'medium',
    3: 'high',
  }
  reasons: any[] = [];
  comment: string | null = null;
  severity!: ('high' | 'low' | 'medium');

  disabledDate = (current: Date): boolean => {
    const now = DateTime.now().setZone(this.feUtil.time_zone);
    const todayEnd = now.endOf('day');
    if (this.metaData.date[0]) {
      const startData = this.metaData.date[0];
      const startDate = DateTime.fromISO(startData).setZone(this.feUtil.time_zone).startOf('day');
      return current < startDate.toJSDate() || current > todayEnd.toJSDate();
    }
    return current > todayEnd.toJSDate();
  }

  private static readonly TABS_PRIVILEGE_MATRIX: { [key: number]: boolean[] } = PERMISSION_CONFIG.TABS_PRIVILEGE_MATRIX;
  private static readonly BUTTONS_PRIVILEGE_MATRIX: { [key: number]: { submit: boolean, approve: boolean, reject: boolean }[] } = PERMISSION_CONFIG.BUTTONS_PRIVILEGE_MATRIX;

  get products(): FormArray {
    return this.affectedProductForm.get('products') as FormArray;
  }

  get isProdImpacted(): string {
    return this.formGroup.get('prod')?.get('isImpacted')?.value as string;
  }

  ngOnInit(): void {
    this.products.valueChanges.pipe(untilDestroyed(this)).subscribe((values: any[]) => {
      this.hasDuplicateExists = values.some((value: any, i: number) =>
        this.hasDuplicateBatch(value?.product_name, value?.batch_no, i)
      );
    });
    this.fetchProducts();
    if (this.feUtil.appId === 1) {
      this.beUtil.customerSvc.reasons().pipe(take(1)).subscribe((res: any) => {
        this.reasons = res?.data;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']?.currentValue) {
      this.formConfig = changes['config']?.currentValue;
    }
    if (changes['metaData']?.currentValue) {
      this.metaData = changes['metaData']?.currentValue;
    }
    if (changes['config']?.currentValue && this.formConfig?.length > 0) {
      this.formGroup = new FormGroup({});
      this.tabs = [];
      for (const form of this.formConfig) {
        const group = new FormGroup({});
        for (const field of form.fields) {
          if (field.type === 'divider' || field.type === 'button') continue;
          const controlState = field?.disabled ? { value: null, disabled: true } : null;
          group.addControl(field.name, new FormControl(controlState, this.getValidators(field.validators)));
        }
        this.formGroup.addControl(form.form_type, group);
        this.tabs.push({ tab_type: 'form', form_name: form.form_name, form_type: form.form_type });
        this.formGroupEvenHandler(group, form.form_type);
      }
      this.tabs.push({ tab_type: 'history', form_name: 'History', form_type: 'history' });
    }
    // Only evaluate visibility logic when metaData is present and current
    if (!changes['metaData']?.currentValue) return;

    const meta = this.metaData;

    // Guard: both inputs must be settled before evaluating form visibility
    if (!meta?.page || !meta?.severity) return;

    const compliance = this.feUtil.user_data.customer.compliance_level;
    const hasAccessToHistory = this.feUtil.roleService.hasAccess([0, 1, 2]);

    // Resolve severity from meta.page — avoids feUtil.selectedApplication race on routing
    if (meta.page === 'tr-incident') {
      this.severity = this.severityMap[meta.severity as number];
    } else if (meta.page === 'storage-incident') {
      this.severity = meta.severity as 'high' | 'low' | 'medium';
    } else {
      return;
    }

    // Transport requires a completed leg before anything can be shown
    const isTransport = meta.page === 'tr-incident';
    const isLegBlocked = isTransport && meta?.leg_status !== 'completed';
    const canShowForm = !isLegBlocked && compliance.includes(this.severity);

    if (isLegBlocked) {
      this.tabs = [];
    } else if (canShowForm) {
      this.loaders['isFormLoading'].loading = true;
      this.getFormData();
    } else if (hasAccessToHistory) {
      this.tabs = this.tabs.filter((t: Tab) => t.tab_type === 'history');
      this.fetchIncidentHistory();
    } else {
      this.tabs = [];
    }
  }

  private formGroupEvenHandler(formGroup: FormGroup, formType: string): void {
    const updateValidators = (control: FormControl | null, shouldRequire: boolean, emitEvent: boolean = false) => {
      if (!control) return;
      if (shouldRequire) {
        control.setValidators(Validators.required);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ onlySelf: true, emitEvent });
    };

    if (formType === 'prod') {
      const isImpactedControl = formGroup.get('isImpacted');
      const shiftedYesNoControl = formGroup.get('shiftedYesNo');
      const conditionalFields = ['shiftedTo', 'shiftTargetName', 'shiftDatetime'];
      const formControlMap: { [key: string]: FormControl | null } = {};

      isImpactedControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value: any) => {
        if (value === 'No') {
          const productDispositionControl = this.formGroup.get('eval')?.get('productDisposition');
          productDispositionControl?.setValue('No Product Affected');
          this.products.clear();
        }
        formGroup.get('shiftedYesNo')?.reset();
        updateValidators(shiftedYesNoControl as FormControl, value === 'Yes', true);
        if(value === 'Yes') this.products.enable({ onlySelf: true, emitEvent: false });
      });

      conditionalFields.forEach(field => {
        formControlMap[field] = formGroup.get(field) as FormControl;
      });

      shiftedYesNoControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value: any) => {
        const shouldRequire = value === 'Yes';
        conditionalFields.forEach(field => {
          updateValidators(formControlMap[field], shouldRequire);
        });
      });
    }

    if (formType === 'capa') {
      const isMalfunctionControl = formGroup.get('isMalfunctionReported');
      const reasonForExcursionControl = formGroup.get('reasonForExcursion');
      isMalfunctionControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value: any) => {
        updateValidators(reasonForExcursionControl as FormControl, value === 'Yes');
      });
    }
  }

  private getValidators(validators: string[] | undefined): ValidatorFn[] {
    if (!validators) return [];
    return validators
      .map(validator => {
        if (validator === 'required') return Validators.required;
        if (validator === 'minlength:3') return Validators.minLength(3);
        return null;
      })
      .filter((v): v is ValidatorFn => v !== null);
  }

  private fetchProducts(): void {
    this.beUtil.customerSvc.getAllProducts()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: any) => {
          if (res?.status !== 200) return;
          if (res?.status === 200 && res?.data) {
            this.masterProductList = (res.data || []).filter((product: any) => product?.is_active);
          }
        }
      })
  }

  private fetchIncidentHistory(): void {
    const id = this.metaData?.page === 'tr-incident' ? this.metaData?.leg_id : this.metaData?.incident_id;
    if (!id) return;
    this.beUtil.incidentSvc.getHistoryData(id)
      .pipe(untilDestroyed(this))
      .subscribe((res: any) => {
        if (res?.status !== 200) return;
        this.history = (res?.data || []).sort((a: any, b: any) => new Date(b?.created_time).getTime() - new Date(a?.created_time).getTime());
        this.lastRejectResubmitLogId = this.history.filter(item => {
          const msg = item?.message?.toLowerCase() || '';
          return msg.includes('reject') || msg.includes('rejected') || msg.includes('resubmission');
        }).map(item => item?.incident_log_id);
        if (this.lastRejectResubmitLogId.length > 0) this.fetchFormDataByLogId(this.lastRejectResubmitLogId[0], 'form');
      })
  }

  protected getFormGroup(formType: string | undefined): FormGroup | null {
    if (!formType) return null;
    return this.formGroup.get(formType) as FormGroup;
  }

  protected addProduct(): void {
    let isValid = true;
    this.products.controls.forEach((control: AbstractControl) => {
      if (!this.feUtil.validate(control as FormGroup)) isValid = false;
    });
    if (!isValid) return;
    const newProductGroup = new FormGroup({
      prod_id: new FormControl(null),
      product_name: new FormControl(null, [Validators.required]),
      batch_no: new FormControl(null, [Validators.required]),
      units_affected: new FormControl(null, [Validators.required, Validators.min(1)]),
      is_selected: new FormControl(0)
    });
    this.products.push(newProductGroup);
    this.watchBatchDuplicates(newProductGroup);
  }
  
  protected hasDuplicateBatch(productName: string, batchNo: string, excludeIndex: number = -1): boolean {
    return this.products.controls.some((ctrl: AbstractControl, i: number) => {
      if (i === excludeIndex) return false;
      const other = ctrl.value;
      return other?.product_name === productName && other?.batch_no === batchNo;
    });
  }

  private warnDuplicateBatch(): void {
    this.feUtil.message.warning('This batch number already exists for the selected product. Please use a different batch number.');
  }

  private validateExistingBatchDuplicates(): void {
    if (this.products.length === 0) return;
    const hasDuplicate = (this.products.value as any[]).some((value: any) =>
      this.hasDuplicateBatch(value.product_name, value.batch_no)
    );
    if (hasDuplicate) this.warnDuplicateBatch();
  }

  private watchBatchDuplicates(form: FormGroup): void {
    form.valueChanges.pipe(untilDestroyed(this)).subscribe((value: any) => {
      if (value?.product_name == null || value?.batch_no == null) return;
      const currentIndex = this.products.controls.indexOf(form);
      if (this.hasDuplicateBatch(value.product_name, value.batch_no, currentIndex)) {
        this.warnDuplicateBatch();
      }
    });
  }

  protected removeProduct(index: number): void {
    this.products.removeAt(index);
  }

  protected openProductModal(): void {
    this.modals['isProductModal'] = true;
    this.showProductTable = false;
  }

  protected handleProductModalCancel(): void {
    // const isInvalid = this.products.controls.some((control: AbstractControl) => !this.feUtil.validate(control as FormGroup));
    // if (isInvalid) return;
    const locAffectedProducts = localStorage.getItem('affectedProducts');
    if (locAffectedProducts && this.products.dirty) {
      this.products.clear();
      let affectedProducts = JSON.parse(locAffectedProducts);
      affectedProducts.forEach((item: any) => {
        this.products.push(new FormGroup({
          prod_id: new FormControl(item?.product_name),
          product_name: new FormControl(item?.product_name),
          batch_no: new FormControl(item?.batch_no),
          units_affected: new FormControl(item?.units_affected),
          is_selected: new FormControl(item?.is_selected),
        }));
      });
      this.modals['isProductModal'] = false;
      return;
    }
    const isProdActionTaken = this.actionDetails['prod']['action_taken_at']
    const isEvalActionTaken = this.actionDetails['eval']['action_taken_at'];
    const isProdModified = this.products.value.some((product: any) => !product?.prod_id);
    const isProdRemoved = this.products.value.length < this.affectedProductList.length;
    if (!isProdActionTaken && !isEvalActionTaken && (isProdModified || isProdRemoved)) {
      this.feUtil.modalSvc.confirm({
        nzTitle: 'Affected product data changed',
        nzContent: `${isProdRemoved ? 'Removed products will be added back.' : 'Newly added products will be removed.'} Do you want to continue?`,
        nzOkText: 'Yes',
        nzCancelText: 'No',
        nzOnOk: () => {
          if (isProdRemoved) {
            this.products.clear();
            this.affectedProductList.forEach((item: any) => {
              this.products.push(new FormGroup({
                prod_id: new FormControl(item?.prod_id),
                product_name: new FormControl(item?.prod_id), // product_id is used as the value for the product_name field
                batch_no: new FormControl(item?.batch_no),
                units_affected: new FormControl(item?.units_affected),
                is_selected: new FormControl(item?.is_selected),
              }));
            });
          }
          if (isProdModified) {
            for (let i = this.products.length - 1; i >= 0; i--) {
              const product = this.products.at(i).value;
              if (!product?.prod_id) {
                this.products.removeAt(i);
              }
            }
          }
          this.modals['isProductModal'] = false;
        },
      });
    } else this.modals['isProductModal'] = false;
  }

  protected saveProducts(): void {
    let isValid = true;
    this.products.controls.forEach((control: AbstractControl) => {
      const valid = this.feUtil.validate(control as FormGroup);
      if (!valid) {
        isValid = false;
      }
    });
    if (!isValid) return;
    if (this.tabs[this.currentTab].form_type === 'eval') {
      this.updateProductDisposition();
      this.saveProductBeforeSubmit();
    }
    localStorage.setItem('affectedProducts', JSON.stringify(this.products.value));
    this.modals['isProductModal'] = false;
  }

  private updateProductDisposition(): void {
    const productSelections: number[] = this.products.value.map((product: any) => product?.is_selected);
    if (!productSelections.every((sel: number) => sel == null)) {
      const isPartial = productSelections.includes(1);
      const isAllRejected = productSelections.every((sel: number) => sel == -1);
      const isAllReleased = productSelections.every((sel: number) => sel == 1);
      if (isPartial) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Partial Release');
      if (isAllRejected) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Rejected all');
      if (isAllReleased) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Release all');
    }
  }

  private saveProductBeforeSubmit(): void {
    const payload: any = {
      products: this.products.value,
      ...(this.feUtil.appId === 2 && {
        leg_id: this.metaData?.leg_id, 
        trip_id: this.metaData?.id,
        prod_submitted_by: this.actionDetails['prod']['submitted_by'],
      }),
      ...(this.feUtil.appId === 1 && { incident_id: this.metaData?.incident_id, prod_submitted_by: this.actionDetails['prod']['submitted_by'] }),
    };
    this.beUtil.incidentSvc.saveProduct(payload).subscribe({
      next: () => {
        this.feUtil.message.success('Product saved successfully')
        this.updateProductDisposition();
        let id = this.feUtil.appId === 2 ? this.metaData?.leg_id : this.metaData?.incident_id;
        if (!id) return;
        this.beUtil.incidentSvc.getHistoryData(id)
          .pipe(untilDestroyed(this))
          .subscribe((res: any) => {
            if (res?.status !== 200) return;
            this.history = (res?.data || []).sort((a: any, b: any) => new Date(b?.created_time).getTime() - new Date(a?.created_time).getTime());
          })
      }
    })
  }

  protected backToExcel(): void {
    this.showProductTable = false;
    this.products.clear();
  }

  // * button handler for submit, approve, reject
  protected btnHandler(currentTab: number, status: number, formType: string = ''): void {
    if (!this.feUtil.validate(this.formGroup.get(this.tabs[currentTab].form_type) as FormGroup)) return;

    const isProdAffected = this.formGroup.get('prod')?.get('isImpacted')?.value as string;
    const noProductAffected = this.products.length === 0;

    if (isProdAffected === 'Yes' && noProductAffected) {
      this.feUtil.message.warning('Please add affected products!');
      return;
    }

    // ! COMMENTING AS CAPA FILES ARE OPTIONAL
    // if(this.tabs[currentTab]?.form_type === 'capa' && this.capaFiles.length === 0 && status === 0) {
    //   this.feUtil.message.warning('Please upload CAPA files!');
    //   return;
    // }

    if (this.tabs[currentTab]?.form_type === 'eval' && this.formGroup.get('eval')?.get('productDisposition')?.value === null) {
      this.feUtil.message.warning('Please complete the product disposition!');
      return;
    }

    this.feUtil.modalSvc.create<GenericConfirmationDialogComponent, TYPE.GenericConfirmationDialog>({
      nzTitle: status == 0 ? 'Please confirm your password' : 'Please confirm your password and add remarks',
      nzContent: GenericConfirmationDialogComponent,
      nzData: { displayInput: status == 0 ? ['password'] : ['password', 'remarks'] },
      nzCentered: true,
      nzMaskClosable: false,
      nzClosable: false,
      nzCloseOnNavigation: true,
    }).afterClose.pipe(untilDestroyed(this)).subscribe({
      next: (res: any) => {
        if (res) {
          if (status === 0) {
            this.loaders[this.tabs[currentTab].form_type].submit = true;
            const keyMap: { [key: string]: string } = { prod: 'excursionDetails', capa: 'capaDetails', eval: 'evalDispDetails' };
            const key = keyMap[this.tabs[currentTab].form_type] || '';
            const payload = {
              form_type: this.tabs[currentTab].form_type,
              incident_id: this.metaData?.incident_id,
              leg_id: this.metaData?.leg_id,
              trip_id: this.metaData?.id,
              form_data: {
                [key]: {
                  ...this.formGroup.get(this.tabs[currentTab].form_type)?.getRawValue(),
                },
              },
              status: status,
              last_update_user: this.feUtil.user_data.user_id,
              submitted_by: this.feUtil.user_data.user_id,
              password: res?.password,
            };
            const products = (this.products.value || []).map((product: any) => {
              return {
                ...product,
                product_name: this.masterProductList.find(p => p.product_id == product?.product_name)?.generic_name,
                units_affected: Number(product.units_affected),
                is_selected: product.is_selected ? Number(product.is_selected) : 0,
                prod_id: product.product_name
              }
            });

            if (products.length > 0 && (payload['form_type'] === 'prod' || payload['form_type'] === 'eval')) {
              payload.form_data['affectedProductList'] = products;
            }
            
            if (this.formGroup.get('prod')?.get('isImpacted')?.value === 'No' && payload['form_type'] === 'eval') {
              payload.form_data['affectedProductList'] = [];
            }

            if (payload['form_type'] === 'prod' && payload.form_data[key]['shiftDatetime']) {
              const shiftDate = payload.form_data[key]['shiftDatetime'];
              if (shiftDate instanceof Date) {
                payload.form_data[key]['shiftDatetime'] = DateTime.fromJSDate(shiftDate)?.toUTC();
              } else if (typeof shiftDate === 'string') {
                payload.form_data[key]['shiftDatetime'] = DateTime.fromISO(shiftDate)?.toUTC();
              }
            }

            if (payload['form_type'] === 'capa' && payload.form_data[key]['capaImpDate']) {
              const capaImpDate = payload.form_data[key]['capaImpDate'];
              if (capaImpDate instanceof Date) {
                payload.form_data[key]['capaImpDate'] = DateTime.fromJSDate(capaImpDate)?.toUTC();
              } else if (typeof capaImpDate === 'string') {
                payload.form_data[key]['capaImpDate'] = DateTime.fromISO(capaImpDate)?.toUTC();
              }
            }
                        
            Object.keys(payload.form_data[key] || {}).forEach(k => {
              if (payload.form_data[key][k] === null) {
                delete payload.form_data[key][k];
              }
            });

            this.beUtil.incidentSvc.submitIncidentForm(payload)
              .pipe(untilDestroyed(this), finalize(() => this.loaders[this.tabs[currentTab].form_type].submit = false))
              .subscribe({
                next: (res: any) => {
                  if (res?.status !== 200) {
                    this.loaders[this.tabs[currentTab].form_type].submit = false;
                    return;
                  }
                  if ((res?.data && res?.data?.message === 'Invalid password')) {
                    this.feUtil.message.error('Invalid password');
                    this.loaders[this.tabs[currentTab].form_type].submit = false;
                    return;
                  }
                  if (payload['form_type'] === 'capa' && this.capaFiles.length > 0) {
                    this.saveCapaFiles().then(val => {
                      if (val) this.getFormData();
                    });
                  } else this.getFormData();
                  this.feUtil.message.success(`Password verification successful!`);
                },
              });
          }
          if (status === 1 || status === -1) {
            this.loaders[formType || this.tabs[currentTab].form_type][status === 1 ? 'approve' : 'reject'] = true;
            let payload = {
              form_type: formType || this.tabs[currentTab].form_type,
              incident_id: this.metaData?.incident_id,
              leg_id: this.metaData?.leg_id,
              trip_id: this.metaData?.id,
              status: status,
              action_by: this.feUtil.user_data.user_id,
              submitted_by: formType ? this.feUtil.user_data.user_id : this.actionDetails[this.tabs[currentTab].form_type]['submitted_by'],
              password: res?.password,
              remarks: res?.remarks
            }
            this.beUtil.incidentSvc.incidentFormAction(payload)
              .pipe(untilDestroyed(this), finalize(() => this.loaders[this.tabs[currentTab].form_type][status === 1 ? 'approve' : 'reject'] = false))
              .subscribe({
                next: (res: any) => {
                  if (res?.status !== 200) {
                    this.loaders[this.tabs[currentTab].form_type].submit = false;
                    return;
                  }
                  if ((res?.data && res?.data?.message === 'Invalid password')) {
                    this.feUtil.message.error('Invalid password');
                    return;
                  }
                  this.feUtil.message.success(`Password verification successful!`);
                  this.getFormData();
                  if (status === -1) this.formGroup.get(formType || this.tabs[currentTab].form_type)?.reset();
                  if (status === -1) this.formGroup.get(formType || this.tabs[currentTab].form_type)?.enable();
                  if (payload['form_type'] === 'final' && status === -1) {
                    this.resetApprovedOrSubmittedForm(payload, 'eval');
                  }
                }
              })
          }
        }
      }
    });
  }

  private resetApprovedOrSubmittedForm(finalPayload: any, resetFormType: string): void {
    let payload = { ...finalPayload, form_type: resetFormType };
    this.beUtil.incidentSvc.incidentFormAction(payload)
      .pipe(untilDestroyed(this), delay(1000))
      .subscribe((res: any) => {
        if (res?.status !== 200) return;
        this.getFormData();
        this.formGroup.get(resetFormType)?.reset();
        this.formGroup.get(resetFormType)?.enable();
      })
  }

  private getFormData(): void {
    this.loaders['isFormLoading'].loading = true;
    const payload = this.metaData?.page === 'tr-incident' ? { leg_id: this.metaData?.leg_id } : { incident_id: this.metaData?.incident_id };
    this.beUtil.incidentSvc.getFormData(payload)
      .pipe(untilDestroyed(this), delay(500), finalize(() => this.loaders['isFormLoading'].loading = false))
      .subscribe(({ data, status }) => {
        if (status !== 200) return;
        this.products.clear();
        this.reportFormData = data;
        this.patchFormData(data, 'CURRENT-DATA');
        this.fetchIncidentHistory();
        this.updateTabs();
      });
  }

  private patchFormData(formData: any | null, type?: 'CURRENT-DATA' | 'REJECTED-DATA'): void {
    if (!formData) return;
    const affectedProducts = formData?.prod?.affectedProductList;
    if (Array.isArray(affectedProducts)) {
      if (
        affectedProducts.length === 1 && 
        !affectedProducts[0]?.product_name &&
        !affectedProducts[0]?.batch_no &&
        !affectedProducts[0]?.units_affected
      ) {
        this.affectedProductList = [];
      } else {
        this.affectedProductList = affectedProducts;
        this.products.clear();
        this.affectedProductList.forEach((item: any = {}) => {
          this.products.push(new FormGroup({
            prod_id: new FormControl(item.prod_id),
            product_name: new FormControl(item.prod_id), // Using prod_id for product_name as before
            batch_no: new FormControl(item.batch_no),
            units_affected: new FormControl(item.units_affected),
            is_selected: new FormControl(item?.isSelected ? item.isSelected : 0),
          }));
        });
      }
    }
    const formTypes = [
      { key: 'prod', detailsKey: 'excursionDetails', patchKey: 'prod' },
      { key: 'capa', detailsKey: 'capaDetails', patchKey: 'capa' },
      { key: 'eval', detailsKey: 'evalDispDetails', patchKey: 'eval' },
    ];
    for (const { key, detailsKey, patchKey } of formTypes) {
      const data = formData?.[key];
      if (data) {
        if (data?.[detailsKey]) {
          this.formGroup.get(patchKey)?.patchValue(data[detailsKey]);
        }
        if (data?.actionDetails) {
          this.actionDetails[key] = data.actionDetails;
        }
      }
    }
    const productSelections: number[] = this.products.value.map((product: any) => product?.is_selected);
    if (!productSelections.length) this.formGroup.get('eval')?.get('productDisposition')?.setValue('No Product Affected');
    if (!productSelections.every((sel: number) => sel == null)) {
      const isPartial = productSelections.includes(1);
      const isAllRejected = productSelections.every((sel: number) => sel == -1);
      const isAllReleased = productSelections.every((sel: number) => sel == 1);
      const isNoAction = productSelections.every((sel: number) => sel == 0);
      if (isPartial) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Partial Release');
      if (isAllRejected) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Rejected all');
      if (isAllReleased) this.formGroup.get('eval')?.get('productDisposition')?.setValue('Release all');
      if (isNoAction) this.formGroup.get('eval')?.get('productDisposition')?.setValue(null);
    }
    if (this.actionDetails && this.actionDetails['prod'] && this.actionDetails['prod']['submitted_at']) {
      this.products.disable();
    } else if (!this.feUtil.roleService.hasAccess([0, 1, 2, 5, 6])) {
      this.products.disable();
    }
    const capaFilesArePresent = Array.isArray(formData?.capa?.documents) && formData.capa.documents.length > 0;
    if (type === 'CURRENT-DATA' && capaFilesArePresent && this.actionDetails['capa'] && this.actionDetails['capa']['submitted_at']) {
      this.capaFiles = formData?.capa?.documents;
    }
    if(type === 'CURRENT-DATA') {
      if (formData?.final) this.actionDetails['final'] = formData.final;
      if (
        this.actionDetails['prod']?.['action_taken_at'] &&
        this.actionDetails['capa']?.['action_taken_at'] &&
        this.actionDetails['eval']?.['action_taken_at']
      ) {
        this.showFinal = true;
      }
    }
  }

  private getFormPrivilege(): boolean[] {
    const privilege = this.feUtil.user_data.userRoles.privilege;
    return (privilege !== -1 && FormsComponent.TABS_PRIVILEGE_MATRIX[privilege] !== undefined) ? FormsComponent.TABS_PRIVILEGE_MATRIX[privilege] : [false, false, false, false];
  }

  private getButtonsPrivilege(): { submit: boolean; approve: boolean; reject: boolean }[] {
    const privilege = this.feUtil.user_data.userRoles.privilege;
    return (privilege !== -1 && FormsComponent.BUTTONS_PRIVILEGE_MATRIX[privilege] !== undefined)
      ? FormsComponent.BUTTONS_PRIVILEGE_MATRIX[privilege]
      : [
        { submit: false, approve: false, reject: false },
        { submit: false, approve: false, reject: false },
        { submit: false, approve: false, reject: false },
      ];
  }

  protected updateTabs(): void {
    if (!this.tabs?.length) return;
    const formPrivilege = this.getFormPrivilege();
    const buttonsPrivilege = this.getButtonsPrivilege();
    this.handleRoleBasedFormConditions(formPrivilege);
    this.handleRoleBasedButtonConditions(buttonsPrivilege);
    if (this.metaData?.hide_forms) {
      this.tabs.forEach((tab: Tab) => {
        this.formGroup.get(tab.form_type)?.disable({ emitEvent: false, onlySelf: true });
      });
      return;
    }
  }

  private fetchFormDataByLogId(logId: number, type: 'form' | 'modal'): void {
    this.beUtil.incidentSvc.getResetFormDataByLogId(logId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ data, status }) => {
          if (status !== 200) return;
          let rejectedFormData = data;
          const keysMap = [
            { key: 'prod', details: 'excursionDetails', actionDetails: 'actionDetails' },
            { key: 'capa', details: 'capaDetails', actionDetails: 'actionDetails' },
            { key: 'eval', details: 'evalDispDetails', actionDetails: 'actionDetails' }
          ];
          if (type === 'form') {
            const actions = [
              { key: 'prod', submitted: 'submitted_at', taken: 'action_taken_at', detail: 'excursionDetails' },
              { key: 'capa', submitted: 'submitted_at', taken: 'action_taken_at', detail: 'capaDetails' },
              { key: 'eval', submitted: 'submitted_at', taken: 'action_taken_at', detail: 'evalDispDetails' },
            ];
            actions.forEach(({ key, submitted, taken, detail }) => {
              if (this.actionDetails[key]?.[submitted] || this.actionDetails[key]?.[taken]) {
                if (rejectedFormData[key]?.hasOwnProperty(detail)) {
                  delete rejectedFormData[key][detail];
                }
              }
            });
            keysMap.forEach(({ key }) => {
              if (rejectedFormData[key]?.hasOwnProperty('actionDetails')) {
                delete rejectedFormData[key]['actionDetails'];
              }
            });
            if (
              this.actionDetails &&
              this.actionDetails['prod'] &&
              this.actionDetails['prod']['submitted_at'] &&
              rejectedFormData &&
              rejectedFormData['prod'] &&
              Object.prototype.hasOwnProperty.call(rejectedFormData['prod'], 'affectedProductList')
            ) {
              delete rejectedFormData['prod']['affectedProductList'];
            }
            if (rejectedFormData?.['capa'] && rejectedFormData?.['capa']?.documents && this.actionDetails['capa'] && !this.actionDetails['capa']['submitted_at']) {
              this.rejectedCapaFiles = rejectedFormData?.['capa']?.documents;
            }
            this.rejectedFormData = rejectedFormData;
            this.patchFormData(this.rejectedFormData, 'REJECTED-DATA');
          }
          if (type === 'modal') {
            if (rejectedFormData) {
              this.rejectedFormData = rejectedFormData;
              this.modals['isFormReportModal'] = true;
            }
          }
        }
      });
  }

  private handleRoleBasedFormConditions(formPrivilege: boolean[]): void {
    this.tabs.forEach((tab, index) => {
      if (tab.tab_type === 'form') {
        const formControl = this.formGroup.get(tab.form_type);
        const isFormSubmitted = this.actionDetails[tab.form_type]?.['submitted_at'];
        if (formControl) {
          if (isFormSubmitted) {
            formControl.disable({ emitEvent: false, onlySelf: true });
          } else {
            formPrivilege[index] ? formControl.disable({ emitEvent: false, onlySelf: true }) : formControl.enable({ emitEvent: false, onlySelf: true });
            tab.disabled = !!formPrivilege[index];
          }
        }
        const isProdSubmitted = this.actionDetails['prod']['submitted_at'];
        const isProdApproved = this.actionDetails['prod']['action_taken_at'];
        const isCapaApproved = this.actionDetails['capa']['action_taken_at'];
        if (tab.form_type === 'eval') {
          tab.disabled = !(isProdApproved && isCapaApproved);
          this.formGroup.get('eval')?.get('productDisposition')?.disable({ emitEvent: false, onlySelf: true }); // making productDisposition field disabled 
        }
        if (tab.form_type === 'capa') tab.disabled = !isProdSubmitted;
      }

      if (tab.tab_type === 'history' && formPrivilege[3]) {
        this.tabs = this.tabs.filter(t => t !== tab);
      }
    });
  }

  private handleRoleBasedButtonConditions(buttonsPrivilege: { submit: boolean; approve: boolean; reject: boolean; product?: boolean }[]): void {
    this.tabs.forEach((tab, index) => {
      if (tab.tab_type === 'form') {
        if (this.metaData?.hide_forms) {
          tab.canSubmit = false;
          tab.canApprove = false;
          tab.canReject = false;
        } else {
          tab.canSubmit = buttonsPrivilege[index].submit;
          tab.canApprove = buttonsPrivilege[index].approve;
          tab.canReject = buttonsPrivilege[index].reject;
          if (buttonsPrivilege[index].product !== undefined) {
            tab.canProduct = buttonsPrivilege[index].product;
          }
        }
      }
    });
  }
  
  protected showRemarks(item: any): void {
    this.feUtil.modalSvc.create({
      nzTitle: 'Remarks',
      nzContent: item?.remarks,
      nzCentered: true,
      nzCancelText: null,
      nzCloseOnNavigation: true,
    });
  }

  protected rejectOrReleaseHandler(status: number): void {
    this.products.controls.forEach((control: AbstractControl) => {
      control.get('is_selected')?.setValue(status);
    });
  }

  protected beforeUploadCapaFile = (file: NzUploadFile, _fileList: NzUploadFile[]): boolean => {
    const maxSizeInMB = 10;
    const maxFiles = 10;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    const size = file.size ?? file.originFileObj?.size ?? 0;
    // Check for max file size
    if (size > maxSizeInBytes) {
      this.feUtil.message.warning(`File size should be 10MB or less. "${file.name}" is too large.`, { nzDuration: 2000 });
      return false;
    }
    // Check for max files
    if (this.capaFiles.length >= maxFiles) {
      this.feUtil.message.warning(`You can upload a maximum of ${maxFiles} files.`, { nzDuration: 2000 });
      return false;
    }
    // Check for duplicate file name
    const newFileName = file.name ?? file.originFileObj?.name;
    if (this.capaFiles.some((f: NzUploadFile) => (f.name || f.originFileObj?.name) === newFileName)) {
      this.feUtil.message.warning(`File "${file.name}" has already been uploaded. Please choose another file.`, { nzDuration: 2000 });
      return false;
    }

    this.capaFiles = [...this.capaFiles, file];
    return false;
  };

  protected saveCapaFiles(): Promise<Boolean> {
    const formData = new FormData();
    const appendFiles = (form: FormData, key: string, files: (File | any)[]) => {
      files.forEach(f => {
        const file = f instanceof File ? f : f.originFileObj;
        if (file) {
          form.append(key, file, file.name);
        }
      });
    };
    let capaFiles = this.capaFiles.map(file => {
      let actualFile = file.originFileObj as File;
      if (!actualFile && file instanceof File) {
        actualFile = file as any;
      }
      if (actualFile && typeof (actualFile as any).uid !== 'undefined') {
        delete (actualFile as any).uid;
      }
      return actualFile || file;
    });
    formData.append('last_update_user', String(this.feUtil.user_data.user_id));
    if(this.feUtil.appId === 2) {
      formData.append('leg_id', String(this.metaData?.leg_id));
    } else if(this.feUtil.appId === 1) {
      formData.append('incident_id', String(this.metaData?.incident_id));
    }
    appendFiles(formData, 'files', capaFiles);
    return new Promise<Boolean>(resolve => {
      this.beUtil.incidentSvc.uploadCapaFile(formData)
        .pipe(untilDestroyed(this), finalize(() => {
          this.capaFiles = [];
          this.modals['isCapaModalVisible'] = false
        }))
        .subscribe({
          next: ({ status }) => {
            if (status !== 200) {
              resolve(false);
              return;
            }
            this.feUtil.message.success('CAPA files uploaded successfully', { nzDuration: 2000 });
            resolve(true);
          },
        });
    });
  }

  protected showRejectedDetails(item: any, type: string): void {
    this.fetchFormDataByLogId(item?.incident_log_id, 'modal');
  }

  protected downloadCapaFiles(): void {
    this.capaFiles.forEach((file: any) => {
      if (file?.doc_link) {
        fetch(file.doc_link)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.doc_name || 'download';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }, 0);
          })
          .catch(() => {
            this.feUtil.message.error('Failed to download file', { nzDuration: 2000 });
          });
      }
    });
  }

  protected fetchPersonnelList(): void {
    this.beUtil.usersSvc.getAllUserByCustomer()
      .pipe(
        untilDestroyed(this),
        map((res: any) => {
          if (res.status !== 200) {
            this.feUtil.message.error(res.message);
            return [];
          }
          return (res.data || []).filter((user: any) => [0, 1, 2, 6].includes(user?.privilege as number))
            .map((user: any) => ({
              ...user,
              username: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
            }));
        }),
        tap((list: any[]) => {
          this.loaders['extension'].modal = false;
          this.qualityPersonnelList = list;
          if (!list.length) {
            this.feUtil.message.error('No CDQA or Admin role user exists in the system to process your extension request.');
            return;
          }
          if (this.flag === 'approved' || this.flag === 'rejected') {
            this.extensionForm.get('admin_id')?.setValue(this.feUtil.user_data.user_id);
            this.extensionForm.get('admin_id')?.disable();
          }
          this.modals['isRequestExtensionModalVisible'] = true;
        })
      )
      .subscribe();
  }

  protected extensionModalHandler(flag: 'request' | 'approved' | 'rejected'): void {
    this.loaders['extension'].modal = true;
    this.flag = flag;
    if (flag === 'approved') {
      const unitControl = this.extensionForm.get('unit');
      const typeControl = this.extensionForm.get('type');
      if (unitControl) {
        unitControl.setValidators([Validators.required, Validators.min(1), Validators.max(60)]);
        unitControl.updateValueAndValidity({ emitEvent: false });
      }
      if (typeControl) {
        typeControl.setValidators([Validators.required]);
        typeControl.updateValueAndValidity({ emitEvent: false });
      }
      this.extensionForm.updateValueAndValidity({ emitEvent: false });
    }
    this.fetchPersonnelList();
  }

  protected requestExtensionHandler(): void {
    if (this.feUtil.validate(this.extensionForm)) {
      this.loaders['extension'].submit = true;
      const payload: any = {
        trip_id: this.metaData?.id,
        leg_id: this.metaData?.leg_id,
        user_id: this.metaData?.extension?.user_id || this.feUtil.user_data.user_id,
        admin_id: this.extensionForm?.getRawValue()?.admin_id,
        flag: this.flag,
        remarks: this.extensionForm.get('remarks')?.value
      }
      if (this.flag === 'approved') {
        payload['no_of_days'] = {
          unit: this.extensionForm.get('unit')?.value,
          type: this.extensionForm.get('type')?.value
        }
      }
      this.beUtil.incidentSvc.TRANSPORT.requestFormExtension(payload)
        .pipe(untilDestroyed(this), finalize(() => this.loaders['extension'].submit = false)).subscribe({
          next: ({ status, message }) => {
            if (status !== 200) {
              this.feUtil.message.error(message);
              return;
            }
            this.feUtil.message.success('Extension request submitted successfully', { nzDuration: 2000 });
            this.modals['isRequestExtensionModalVisible'] = false;
            this.extensionForm.reset();
            this.reload.emit('REFRESH-BY-ID');
          }
        });
    }
  }

  protected handleResubmit(): void {
    this.feUtil.modalSvc.create<GenericConfirmationDialogComponent, TYPE.GenericConfirmationDialog>({
      nzTitle: 'Please confirm your password and add remarks',
      nzContent: GenericConfirmationDialogComponent,
      nzData: { displayInput: ['password', 'remarks'] },
      nzCentered: true,
      nzMaskClosable: false,
      nzClosable: false,
      nzCloseOnNavigation: true,
    }).afterClose.pipe(untilDestroyed(this)).subscribe({
      next: (data) => {
        if (!data) return;
        const { password, remarks } = data;
        if (!password || !remarks) {
          this.feUtil.message.error('Please enter password and remarks');
          return;
        }
        const forms = [];
        if (this.actionDetails['prod']['action_taken_at'] || this.actionDetails['prod']['submitted_at']) forms.push('prod');
        if (this.actionDetails['capa']['action_taken_at'] || this.actionDetails['capa']['submitted_at']) forms.push('capa');
        if (this.actionDetails['eval']['action_taken_at'] || this.actionDetails['eval']['submitted_at']) forms.push('eval');
        const payload: any = {
          forms: forms,
          remarks: remarks,
          password: password,
          last_update_user: this.feUtil.user_data.user_id
        };
        if(this.feUtil.appId === 2) {
          payload['trip_id'] = this.metaData?.id;
          payload['leg_id'] = this.metaData?.leg_id;
        }
        if(this.feUtil.appId === 1) {
          payload['incident_id'] = this.metaData?.incident_id;
        }
        this.loaders['isResubmit'].loading = true;
        this.beUtil.incidentSvc.resubmitIncident(payload)
        .pipe(untilDestroyed(this), finalize(() => this.loaders['isResubmit'].loading = false))
        .subscribe({
          next: (res: any) => {
            if (res?.data.message == 'Invalid password') {
              this.feUtil.message.error('Invalid password');
              return;
            }
            this.feUtil.message.success('Incident resubmission requested successfully', { nzDuration: 2000 });
            this.getFormData();
            this.currentTab = 0;
          }
        });
      }
    })
  }

  protected viewReport(): void {
    this.isViewReport = true;
    this.modals['isFormReportModal'] = true;
  }

  protected isRequired(formType: string, controlName: string): boolean {
    return this.formGroup.get(formType)?.get(controlName)?.hasValidator(Validators.required) || false;
  }

  protected getProductName(prod_id: number): string {
    return this.masterProductList.find(p => p.product_id == prod_id)?.generic_name || '';
  }

  protected addComment(): void {
    if (!this.comment) {
      this.feUtil.message.error('Please enter a comment');
      return;
    }
    let payload: any = { message: this.comment };
    if(this.feUtil.appId === 2) {
      payload['trip_id'] = this.metaData?.id;
      payload['leg_id'] = this.metaData?.leg_id;
    }
    if(this.feUtil.appId === 1) {
      payload['incident_id'] = this.metaData?.incident_id;
    }
    this.beUtil.incidentSvc.comment(payload)
    .pipe(untilDestroyed(this), finalize(() => this.loaders['isComment'].loading = false))
    .subscribe({
      next: ({ status }) => {
        if (status !== 200) return;
        this.comment = null;
        this.feUtil.message.success('Comment added successfully', { nzDuration: 2000 });
        this.fetchIncidentHistory();
      }
    });
  }

  protected capaModalHandler(): void {
    if (this.actionDetails['capa']['submitted_at'] && this.capaFiles.length > 0) {
      this.saveCapaFiles();
      this.getFormData();
      this.modals['isCapaModalVisible'] = false;
    } else this.modals['isCapaModalVisible'] = false;
  }

  /**
   * Triggers generation and download of the incident form report PDF using the PDF service.
   * Uses current view report or rejected form data and metadata for the filename.
   */
  protected async downloadReport(): Promise<void> {
    const formData = this.isViewReport ? this.reportFormData : this.rejectedFormData;
    const metaData: MetaData = {
      ...this.metaData,
      page: this.metaData?.page as 'tr-incident' | 'storage-incident',
    };
    this.loaders['isDownloading'].loading = true;
    const success = await this.incidentFormPdfService.generateAndDownload(formData, metaData, this.graphTemplate);
    if (!success) {
      this.loaders['isDownloading'].loading = false;
      return;
    }
    this.loaders['isDownloading'].loading = false;
  }
  
  EXPECTED_HEADERS = ['Product Name', 'Batch No.', 'No. Of Units Affected'];
  protected downloadTemplate(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([this.EXPECTED_HEADERS]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Affected_products_list');
    XLSX.writeFile(wb, 'Affected_products_list.xlsx');
  }

  onProductFileChange = (event: any) => {
    this.showProductTable = true;
    const file = event.target.files[0];
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx')) {
      this.feUtil.message.error('You can only upload Excel files!');
      return;
    }
    this.readExcelFile(file);
  }

  private readExcelFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.xlsx')) {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const excelData: (string | number)[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as (string | number)[][];
        this.mapExcelDataToAffectedProductList(excelData);
      } else if (fileName.endsWith('.csv')) {
        const csvData = e.target.result;
        const parsedData: (string | number)[][] = XLSX.utils.sheet_to_json(XLSX.read(csvData, { type: 'string' }).Sheets['Sheet1'], { header: 1 });
        this.mapExcelDataToAffectedProductList(parsedData);
      } else {
        this.feUtil.message.error(`Unsupported file type.`);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  mapExcelDataToAffectedProductList(data: (string | number)[][]) {
    const headers = data[0] as string[];
    const headersMatch = this.EXPECTED_HEADERS.every((header, index) => header === headers[index]);
    if (!headersMatch) {
      this.feUtil.message.error('Header Does not match.');
      return;
    }
    const rows = data.slice(1) as (string | number)[][];
    rows.forEach((row) => {
      const productName = row[headers.indexOf('Product Name')] as string;
      const batchNo = String(row[headers.indexOf('Batch No.')]);
      const unitsAffected = row[headers.indexOf('No. Of Units Affected')] as number;
      const product = this.masterProductList.find(p => p.generic_name === productName);
      if (!product) {
        this.feUtil.message.error('Product name does not match.');
        return;
      }
      const newProductGroup = new FormGroup({
        prod_id: new FormControl(product.product_id),
        product_name: new FormControl(product.product_id),
        batch_no: new FormControl(batchNo),
        units_affected: new FormControl(unitsAffected),
        is_selected: new FormControl(0),
      });
      this.products.push(newProductGroup);
      this.validateExistingBatchDuplicates();
    });
  }

  constructor(
    protected readonly feUtil: FeUtil,
    private readonly beUtil: BeUtil,
    private readonly incidentFormPdfService: IncidentFormPdfService
  ) { }
}